import React, { useEffect, useRef, useState } from 'react';
import SongLibraryModal from './SongLibraryModal';
import YouTubeSearchModal from './YouTubeSearchModal';
import { updateAssetSongs, buildMovieKey } from '../data/firestore';
import { uploadFile, cdnUrl } from '../data/uploadService';
import { buildViewerSong } from '../utils/viewerSongs';

const SOURCE_BADGE = { upload: 'Library', youtube: 'YouTube' };
let seq = 0;
const makeId = () => `vs${Date.now()}${seq++}`;
const fmt = (s) => {
  if (!s) return '';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};
function readFileDuration(file) {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => resolve(Number.isFinite(el.duration) ? el.duration : 0);
    el.onerror = () => resolve(0);
    el.src = URL.createObjectURL(file);
  });
}

// Owner-only editor for a single clip's viewer-selectable soundtracks (up to 4)
// plus which one plays first. Persists straight to the asset doc.
export default function ClipSoundtrackEditor({ tripId, asset }) {
  const [songs, setSongs] = useState(asset.viewerSongs || []);
  const [def, setDef] = useState(asset.viewerSongDefault || null);
  const [picking, setPicking] = useState(false);
  const [pickingYt, setPickingYt] = useState(false);
  const [upBusy, setUpBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef(null);

  // Re-seed when the underlying asset changes identity (navigating clips).
  useEffect(() => {
    setSongs(asset.viewerSongs || []);
    setDef(asset.viewerSongDefault || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  const full = songs.length >= 4;
  const add = (s) => setSongs((l) => (l.length >= 4 ? l : [...l, s]));
  const remove = (id) => {
    setSongs((l) => l.filter((s) => s.id !== id));
    setDef((d) => (d === id ? null : d));
  };
  const rename = (id, label) => setSongs((l) => l.map((s) => (s.id === id ? { ...s, label } : s)));

  const onLibrary = (lib) => {
    setPicking(false);
    add(buildViewerSong(makeId, lib));
  };
  const onYouTube = (r) => {
    setPickingYt(false);
    add(buildViewerSong(makeId, { ...r, source: 'youtube', label: r.title }));
  };
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file || full) return;
    setErr(null);
    setUpBusy(true);
    try {
      const durationSec = await readFileDuration(file).catch(() => 0);
      const key = buildMovieKey(`clip-${asset.id}`, 'audio/', file.name);
      await uploadFile(file, { key, contentType: file.type || 'audio/mpeg', bucket: 'movies' });
      add(buildViewerSong(makeId, { source: 'upload', label: file.name.replace(/\.[^.]+$/, ''), url: cdnUrl(key), songDurationSec: durationSec || 0 }));
    } catch (e2) {
      setErr(e2.message || String(e2));
    } finally {
      setUpBusy(false);
    }
  };

  const save = async () => {
    setErr(null);
    try {
      await updateAssetSongs(tripId, asset.id, { viewerSongs: songs, viewerSongDefault: def });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e2) {
      setErr(e2.message || String(e2));
    }
  };

  return (
    <div className="cs-section" style={{ marginTop: 20 }}>
      <div className="cs-section-head">
        <h2 style={{ fontSize: 18 }}>Viewer soundtrack options</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={full} onClick={() => setPicking(true)}>＋ Library</button>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={full} onClick={() => setPickingYt(true)}>＋ YouTube</button>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={upBusy || full} onClick={() => fileRef.current?.click()}>
            {upBusy ? 'Uploading…' : '＋ Upload'}
          </button>
          <input ref={fileRef} type="file" accept="audio/*" style={{ display: 'none' }} onChange={onUpload} />
        </div>
      </div>
      <p className="cs-section-sub">
        Up to 4 tracks viewers can pick from when watching this clip. The chosen song plays at normal
        speed and loops (even if the clip is sped up); the clip’s own audio is muted while it plays.
      </p>
      {err && <div className="cs-help" style={{ color: '#e88', marginBottom: 8 }}>{err}</div>}
      {songs.length === 0 ? (
        <div className="cs-empty">No songs yet. Viewers hear the clip’s own audio.</div>
      ) : (
        <>
          {songs.map((s, i) => {
            const isDefault = def ? def === s.id : i === 0;
            return (
              <div key={s.id} className="cs-songrow" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button className={`cs-btn cs-btn-sm ${isDefault ? 'cs-btn-primary' : 'cs-btn-ghost'}`} title={isDefault ? 'Plays first' : 'Make default'} onClick={() => setDef(s.id)}>
                  {isDefault ? '★ Default' : '☆ Default'}
                </button>
                <input className="cs-input" style={{ flex: '1 1 180px' }} value={s.label} onChange={(e) => rename(s.id, e.target.value)} />
                <span className="cs-badge">{SOURCE_BADGE[s.source] || s.source}</span>
                <span className="cs-help">{s.songDurationSec ? fmt(s.songDurationSec) : ''}</span>
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => remove(s.id)}>Remove</button>
              </div>
            );
          })}
          <div className="cs-songrow" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button className={`cs-btn cs-btn-sm ${def === 'orig' ? 'cs-btn-primary' : 'cs-btn-ghost'}`} title="Start on the clip's own audio" onClick={() => setDef('orig')}>
              {def === 'orig' ? '★ Default' : '☆ Default'}
            </button>
            <span style={{ flex: '1 1 180px', color: 'var(--muted)' }}>Original audio (no song)</span>
          </div>
        </>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
        <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={save}>Save soundtrack</button>
        {saved && <span className="cs-help" style={{ color: 'var(--surf)' }}>Saved ✓</span>}
        <span className="cs-help">{songs.length}/4</span>
      </div>
      {picking && <SongLibraryModal onClose={() => setPicking(false)} onPick={onLibrary} />}
      {pickingYt && <YouTubeSearchModal onClose={() => setPickingYt(false)} onPick={onYouTube} />}
    </div>
  );
}
